import { useState, useEffect, useMemo, useRef } from 'react';
import HighPriceWarningPopover from './HighPriceWarningPopover';
import { loadPricingDoc } from '../hooks/useReverbPricing';
import {
  pricingIdentityKey,
  getPricingEntry,
  isPastTtl,
  subscribeReturnToView,
} from '../utils/pricingDocCache';
import {
  formatDollarsFromCents,
  formatMarketLabel,
  getDisplayTier,
  evaluateMarketTier,
  buildReverbSearchUrl,
  shouldShowLimitedNote,
  normalizeDecade,
  nearestConditionAskContext,
} from '../utils/reverbPricing';

// Palette mirrors the effect-type chip treatment in AddPedalForm so the bar
// reads as "selected/unselected pill chips" — a familiar interaction visually
// even though it's read-only here.
//   - active*  : SELECTED effect-type chip (gold border, light gold fill, dark
//                gold text). Applied to whichever zone the price falls in.
//   - inactive*: UNSELECTED effect-type chip (gray border, white fill, slate
//                text). Applied to the two zones the price is NOT in.
// rangeText reuses the navbar link blue so $low / $high read as platform
// chrome. The triangle pointer uses the same gold (#C9A227) as the active
// chip border so the whole "you are here" affordance reads as one unified
// gold accent.
const COLORS = {
  activeBorder:   '#C9A227',
  activeBg:       'rgba(201, 162, 39, 0.12)',
  activeText:     '#946F00',
  inactiveBorder: '#d1d5db',
  inactiveBg:     'white',
  inactiveText:   '#374151',
  triangle:       '#C9A227',
  rangeText:      '#5A9ECB',
  headerText:     '#374151',
  attrText:       '#6B7280'
};

// Debounce window for the on-demand pricing lookup. AddPedalForm passes the
// make/model fields straight through as the user types, so without a debounce
// the callable fired once per keystroke — hammering Reverb's rate limit and
// persisting junk truncated-model docs (e.g. "..._d_modern", "..._do_modern")
// that then got refreshed by the weekly sweep forever. Waiting for a ~500ms
// pause coalesces a burst of typing into a single lookup for the final value.
const PRICE_LOOKUP_DEBOUNCE_MS = 500;

// Same-height loading placeholder for the full (banner) variant so the Step-3
// price screen never shows a blank gap or jumps when the range arrives.
// Dimensions mirror the gold banner below (~86px rounded card + attribution
// line). Compact (PedalDetails) never uses this — it keeps returning null
// while loading so the inline pill doesn't reserve space. Callers that aren't a
// dedicated price screen should pass showSkeleton={false}: reserving space and
// then collapsing it reads as a broken loader rather than an absent banner.
function PriceCheckSkeleton({ style }) {
  return (
    <div
      style={{ marginBottom: '1rem', fontFamily: 'Nunito, sans-serif', userSelect: 'none', ...style }}
      aria-hidden="true"
    >
      <style>{`
        @keyframes pxcPriceShimmer { 0% { background-position: -240px 0; } 100% { background-position: 240px 0; } }
        .pxc-price-sk {
          background: linear-gradient(90deg, #eef0f2 25%, #e3e6e9 37%, #eef0f2 63%);
          background-size: 480px 100%;
          animation: pxcPriceShimmer 1.2s ease-in-out infinite;
          border-radius: 6px;
        }
      `}</style>
      <div className="pxc-price-sk" style={{ height: 86, borderRadius: 12 }} />
      <div style={{ textAlign: 'center', marginTop: 6 }}>
        <span className="pxc-price-sk" style={{ display: 'inline-block', height: 12, width: 180 }} />
      </div>
    </div>
  );
}

// `pricingDocOverride` bypasses the network fetch and renders directly from a
// supplied pedalPricing doc. Used ONLY by the dev-only preview route (real
// screenshots of real components without Firestore writes); production
// callers never pass it.
// `listingId` is the listing being evaluated (listing page, offer pages):
// review finding 3 — the component must exclude that listing's own PXC
// evidence from its benchmark, like the badge and offer hooks already do.
// AddPedalForm has no listing yet and passes nothing.
function PriceIndicator({ make, model, condition, price, decade, style, compact = false, priceInputRef, showSkeleton = true, listingId = null, pricingDocOverride = null }) {
  const [pricingDoc, setPricingDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  // Mirrors pricingDoc but is NOT an effect dep, so the fetch effect can tell
  // "first load for this identity" (no doc yet → show the skeleton immediately,
  // even during the debounce window) apart from "refetch" (decade changed but a
  // bar is already on screen → keep it up until the new data lands, so there's
  // no flash-to-skeleton and no layout jump).
  const pricingDocRef = useRef(null);

  // Normalized once per render so the effect dep + caption use the same value
  // the server will see. Empty / 'Any' / unrecognized collapses to 'modern'.
  const normDecade = normalizeDecade(decade);

  // One network call per (make, model, decade). Switching condition uses the
  // already-fetched doc without re-calling the callable; switching decade DOES
  // refetch because the server keys / queries differently per era.
  useEffect(() => {
    if (pricingDocOverride) {
      pricingDocRef.current = pricingDocOverride;
      setPricingDoc(pricingDocOverride);
      setLoading(false);
      return;
    }
    const trimmedMake = (make || '').trim();
    const trimmedModel = (model || '').trim();
    if (!trimmedMake || !trimmedModel) {
      pricingDocRef.current = null;
      setPricingDoc(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    // First load for this identity (no doc yet) → flip to loading immediately so
    // the skeleton fills the space through the debounce window as well. On a
    // refetch (decade changed) we already have a bar, so we DON'T flip to
    // loading — the old bar stays visible until the new data lands (no
    // flash-to-skeleton, no jump). Reading the ref (not state) keeps this out of
    // the dep array while still seeing the latest resolved doc.
    if (!pricingDocRef.current) setLoading(true);

    // Debounce: defer the actual callable until the user has paused typing for
    // PRICE_LOOKUP_DEBOUNCE_MS. A subsequent keystroke re-runs the effect, whose
    // cleanup clears this pending timer AND sets cancelled=true before the next
    // run starts — so only the final settled (make, model, decade) triggers a
    // network call, and a superseded in-flight response can never call setState
    // (guards below). This is explicit newest-wins, not "whichever resolves last".
    const timerId = setTimeout(() => {
      (async () => {
        try {
          // loadPricingDoc (hooks/useReverbPricing.js) is the shared callable
          // path: dev-override bypass first (without it, detail pages showed
          // stale v1 ranges beside badges computed from v2 override data —
          // two sources of truth on one screen, caught in the owner's local
          // pass), then fetchReverbPricingOnDemand, DEDUPLICATED BY PRICING
          // IDENTITY through pricingDocCache's in-flight map.
          //
          // WHY DEDUPE MATTERS HERE: PedalDetails renders this component
          // TWICE (one of the two is hidden by a CSS media query), so a
          // listing view used to cost two callable invocations for the same
          // make/model/decade. Both instances now join one request.
          //
          // IT IS A FETCH, NOT A CACHE READ, ON PURPOSE: this component does
          // not short-circuit on a fresh cache entry at mount. The Add Pedal
          // price check recovers from the callable's ~6-7s cold start by the
          // user going back and retrying (see prewarmReverbPricing), and a
          // cached null would silently kill that retry. The shared cache
          // stamp is used only to GATE the refresh below.
          const data = await loadPricingDoc(trimmedMake, trimmedModel, normDecade);
          if (cancelled) return;
          pricingDocRef.current = data || null;
          setPricingDoc(data || null);
        } catch (err) {
          // Silent failure by design — never surface this to the listing form.
          if (!cancelled) {
            pricingDocRef.current = null;
            setPricingDoc(null);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, PRICE_LOOKUP_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [make, model, normDecade, pricingDocOverride]);

  // BOUNDED REFRESH ON RETURN TO VIEW (owner instruction, 2026-10-12:
  // "Implement a bounded refresh on pricing-screen re-entry or return to
  // visibility, deduplicated by pricing identity").
  //
  // Before this, the ONLY thing that ever started a pricing fetch was a
  // lookup running — a mount, or an identity change. A listing page or the
  // Add Pedal price screen left open showed whatever document it fetched
  // when it opened, for as long as it stayed open, however many transactions
  // settled server-side in the meantime. (Screen RE-ENTRY inside the SPA
  // already remounts this component, so that half was and is covered by the
  // mount lookup; what was missing is the user coming BACK to a screen that
  // never unmounted — another tab, another app, the phone locked.)
  //
  // Three things keep this bounded, and the common case free:
  //   - It is an EVENT, never a poll: one shared visibilitychange listener
  //     (plus the native app-resume signal under Capacitor) in
  //     pricingDocCache.js, fanned out to subscribers. Nothing is on an
  //     interval, and Browse thumbnails do not subscribe at all.
  //   - TTL GATE: the shared store's fetch stamp for THIS pricing identity
  //     must be older than PRICING_DOC_CACHE_TTL_MS — the same 10-minute
  //     rule the lookup path already uses, not a second rule. Flipping back
  //     to a tab you left two minutes ago costs nothing.
  //   - DEDUPE BY IDENTITY: the refetch goes through the same in-flight map
  //     as the mount lookup, so the two instances on a listing page (or any
  //     N components on one identity) produce ONE request.
  //
  // And it never degrades what is on screen: no setLoading, so the current
  // value stays rendered while the request is in flight, and a null/failed
  // refresh is discarded rather than blanking a working display. Withdrawing
  // data that must no longer be shown is the expiry stamp's job (below),
  // which needs no network at all.
  useEffect(() => {
    if (pricingDocOverride) return undefined; // dev preview: never networks
    const trimmedMake = (make || '').trim();
    const trimmedModel = (model || '').trim();
    if (!trimmedMake || !trimmedModel) return undefined;
    const identityKey = pricingIdentityKey(trimmedMake, trimmedModel, normDecade);
    let cancelled = false;
    const unsubscribe = subscribeReturnToView(() => {
      // Nothing displayed for this identity yet (first lookup still
      // debouncing / in flight, or it resolved to no document): that lookup
      // owns the first load. A refresh exists to freshen a value the user is
      // looking at, so with nothing held there is nothing to refresh and no
      // request is made.
      if (!pricingDocRef.current) return;
      const entry = getPricingEntry(identityKey);
      // No fetch stamp for THIS identity yet. The held document belongs to a
      // previous identity (the decade just changed and the new lookup is
      // still inside its debounce window), so that lookup owns this one and
      // a refresh here would just be a second request for it.
      if (!entry) return;
      if (!isPastTtl(entry.at)) return; // inside the TTL → no network
      loadPricingDoc(trimmedMake, trimmedModel, normDecade)
        .then((data) => {
          if (cancelled || !data) return; // keep the current value
          pricingDocRef.current = data;
          setPricingDoc(data);
        })
        .catch(() => { /* keep the current value */ });
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [make, model, normDecade, pricingDocOverride]);

  // v2 gating (REVERB_PRICING_AUDIT_2026-10-08.md): what this component may
  // render is decided by evaluateMarketTier, the single shared evaluator —
  //   'verdict' → the zoned Good/Fair/High treatment + high-price warning
  //   'single'  → one market price (every observed listing at the same price);
  //               never an invented spread or zones
  //   'range' / 'legacy' → an asking-price range with its limitations; no
  //               verdict framing anywhere
  //   'none'    → nothing (or the loading skeleton)
  // Expiry re-evaluation (R-1 rev 2/3 review findings): useMemo reuses its
  // result until a dependency changes, so TIME PASSING alone would never
  // re-run the evaluator — a mounted display could keep an expired
  // transaction-informed band on screen indefinitely.
  //
  // BOUNDARY CONTRACT (one rule across stamps, readers, timers): an entry
  // is valid THROUGH staleAfterMs inclusive and expired strictly after it
  // (the reader rejects on now > staleAfterMs). The scheduler below only
  // bumps once Date.now() is strictly past the stamp; a callback landing
  // exactly AT the stamp reschedules a 1ms recheck instead of leaving a
  // still-valid entry with no pending timer.
  //
  // SCHEDULING: self-rechecking chunks (never one long setTimeout) —
  // browser timers cap at ~24.8 days and background tabs throttle, so each
  // chunk re-measures the remaining time and either bumps (past the
  // stamp), or re-arms. The shared return-to-view signal re-checks when the
  // user comes back (tab visible, or the native app resumed), covering
  // throttled/suspended timers. Cleanup clears the pending chunk and
  // unsubscribes; any evaluation change (new doc, condition, listing)
  // re-arms against the new stamp.
  //
  // This path NEVER fetches: it re-runs the evaluator over the document
  // already in state, which is the only mechanism that can REMOVE an
  // expired transaction-informed band. It is independent of the bounded
  // refresh above — an expired band disappears whether or not any network
  // call happens, and inside the TTL no network call does.
  const [expiryTick, setExpiryTick] = useState(0);
  const evaluation = useMemo(
    () => evaluateMarketTier(pricingDoc, condition, { excludeListingId: listingId }),
    [pricingDoc, condition, listingId, expiryTick]
  );
  useEffect(() => {
    if (!evaluation.transactionInformed || !Number.isFinite(evaluation.staleAfterMs)) {
      return undefined;
    }
    const stamp = evaluation.staleAfterMs;
    const EXPIRY_RECHECK_CHUNK_MS = 6 * 60 * 60 * 1000;
    let timerId = null;
    let disposed = false;
    const arm = () => {
      if (disposed) return;
      const remaining = stamp - Date.now();
      if (remaining < 0) {
        setExpiryTick((t) => t + 1); // strictly past the stamp: re-evaluate
        return;
      }
      // Still valid (the stamp instant included): recheck after the last
      // valid instant, in bounded chunks.
      timerId = setTimeout(arm, Math.min(remaining + 1, EXPIRY_RECHECK_CHUNK_MS));
    };
    const onReturnToView = () => {
      if (Date.now() > stamp) setExpiryTick((t) => t + 1);
    };
    arm();
    const unsubscribe = subscribeReturnToView(onReturnToView);
    return () => {
      disposed = true;
      if (timerId) clearTimeout(timerId);
      unsubscribe();
    };
  }, [evaluation]);
  const isVerdict = evaluation.kind === 'verdict';
  const isSingle = evaluation.kind === 'single';
  const tier = evaluation.kind === 'none' ? null : evaluation.tier;
  // Nearest-condition asking context for an EMPTY result (see the render
  // branch below). Computed outside the evaluation on purpose: it is never
  // part of what evaluateMarketTier returns to any consumer.
  const conditionContext = useMemo(
    () => (evaluation.kind === 'none' ? nearestConditionAskContext(pricingDoc, condition) : null),
    [pricingDoc, condition, evaluation.kind]
  );
  // Owner display policy (2026-10-09): NO evidence provenance in any
  // customer-facing title/tooltip/accessibility text — on ANY device.
  // buildMarketTooltip remains an INTERNAL formatter (dev tooling/tests)
  // and is no longer rendered. Accessible PRICE information is preserved
  // via a plain label: the price (or range) plus, when applicable, the
  // same generic caveat the sighted user sees. Nothing else.
  const accessiblePriceLabel = (() => {
    const base = evaluation.kind === 'single'
      ? `Market price ${formatDollarsFromCents(evaluation.priceCents)}`
      : tier
        ? `Market price ${formatDollarsFromCents(tier.low)} to ${formatDollarsFromCents(tier.high)}`
        : 'Market price unavailable';
    return shouldShowLimitedNote(evaluation)
      ? `${base}. This estimate is based on limited comparable data.`
      : base;
  })();
  // Attribution market segment: old-format/stale data shows NO era label to
  // customers (neither "prior data" nor "Current market"); the internal
  // legacy classification and its safeguards are unchanged.
  const isPriorData = Boolean(evaluation.info && evaluation.info.priorData);
  const attributionMarket = isPriorData ? null : formatMarketLabel(normDecade);
  // The attribution link opens THIS pedal's Reverb search with the filters
  // the benchmark used (never the homepage); for mixed Excellent + Mint
  // evidence the condition filter is dropped to match the broader basis.
  const reverbSearchUrl = buildReverbSearchUrl({
    make,
    model,
    condition,
    normDecade,
    mixedConditions: Boolean(evaluation.info && evaluation.info.mixedConditions),
  });

  // When stored median === 75th pct, Fair is recalculated as the low–high
  // midpoint (display-only) so the bar never shows duplicate Fair/High labels.
  // Only the verdict presentation uses this; ranges show low/high as-is.
  const displayTier = useMemo(() => getDisplayTier(tier), [tier]);

  const priceCents = Math.round((Number(price) || 0) * 100);
  const hasPrice = priceCents > 0;
  const [warningDismissed, setWarningDismissed] = useState(false);

  /**
   * The warning threshold is the upper endpoint the CUSTOMER ACTUALLY SEES
   * (owner, 2026-10-12). Bands are stored in cents but displayed rounded to
   * whole dollars by formatDollarsFromCents, so a band ending at 10350
   * reads "$104" on screen. Comparing against the raw 10350 made $104 warn
   * while the screen said the range went up to $104 — contradictory to the
   * customer. The comparison therefore uses the DISPLAYED dollar value:
   * anything at or below it is inside the range, anything above it warns
   * ($104 → no warning, $104.01 → warning).
   * This changes only WHEN the warning appears. The band values, the
   * evidence weighting, the publication gate, the caveat rules and the
   * attribution are all untouched.
   */
  const warnAboveCents = tier && Number.isFinite(tier.high)
    ? Math.round(tier.high / 100) * 100
    : null;

  useEffect(() => {
    // Re-arm the dismissed warning whenever the price leaves the warnable
    // region, so re-entering it shows the warning again. Uses the SAME
    // displayed-endpoint threshold as the warning itself.
    const warnable =
      (isVerdict || isSingle || evaluation.kind === 'range') &&
      warnAboveCents !== null && priceCents > warnAboveCents;
    if (!warnable) setWarningDismissed(false);
  }, [priceCents, isVerdict, isSingle, evaluation, warnAboveCents]);

  // No usable bar yet. While the first lookup for this identity is in flight we
  // render a same-height skeleton (full variant only) so the price screen never
  // pops or jumps when the bands arrive. Once the lookup has RESOLVED with no
  // usable data (loading === false) we render nothing. The price INPUT lives in
  // AddPedalForm, never inside this component, so manual price entry is always
  // available regardless of this state.
  if (!displayTier && !isSingle) {
    if (!compact && loading && showSkeleton) return <PriceCheckSkeleton style={style} />;
    // Compact pill (listing page): while the first lookup is in flight, hold
    // an invisible box the size of the pill so the price row does not grow
    // (and wrap) when the market price arrives (2026-10-31). If the lookup
    // resolves with no usable data the box is released, as before.
    if (compact && loading) {
      return <span aria-hidden="true" style={{ display: 'inline-flex', height: 30, minWidth: 160, flexShrink: 0, visibility: 'hidden', ...style }} />;
    }
    // CONTEXT ONLY (reviewer boundary, 2026-10-12): the chosen condition has
    // no pricing, so name the nearest condition that does, as an explicitly
    // labelled ASKING comparison. It is not a valuation: no warning, no
    // verdict, no trade balance reads it (evaluateMarketTier never returns
    // it). Full variant only — the Add Pedal price step is where an empty
    // field needs explaining (Death By Audio Thee Treble Overload, Very
    // Good: Reverb US had 25 listings, none Very Good).
    if (!compact && !loading && conditionContext) {
      // Owner, 2026-10-12: same gold card and type scale as every other
      // Add Pedal price display — headline in the large gold style, the
      // comparison on the second line in the "Market price" line style.
      const ctxValue = conditionContext.single
        ? formatDollarsFromCents(conditionContext.low)
        : `${formatDollarsFromCents(conditionContext.low)} to ${formatDollarsFromCents(conditionContext.high)}`;
      const headline = `No ${condition} market pricing available.`;
      const detailLabel = `${conditionContext.condition}-condition market price:`;
      return (
        <div
          style={{ marginBottom: '1rem', fontFamily: 'Nunito, sans-serif', userSelect: 'none', ...style }}
          aria-label={`${headline} ${detailLabel} ${ctxValue}.`}
        >
          <div
            style={{
              backgroundColor: COLORS.activeBg,
              border: `1.5px solid ${COLORS.activeBorder}`,
              borderRadius: 12,
              padding: '0.85rem 1rem',
              textAlign: 'center'
            }}
          >
            <div
              style={{
                fontSize: '1.45rem',
                fontWeight: 800,
                color: COLORS.activeText,
                lineHeight: 1.2
              }}
            >
              {headline}
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: '0.9rem',
                fontWeight: 600,
                color: COLORS.headerText
              }}
            >
              {/* Label and value are separate non-wrapping elements, so a
                  narrow screen breaks between them, never inside the range. */}
              <span style={{ whiteSpace: 'nowrap' }}>{detailLabel}</span>{' '}
              <span style={{ whiteSpace: 'nowrap' }}>{ctxValue}.</span>
            </div>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: COLORS.attrText, textAlign: 'center' }}>
            <a
              href={buildReverbSearchUrl({ make, model, condition: conditionContext.condition, normDecade })}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: COLORS.rangeText, textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '2px' }}
            >
              Reverb listings
            </a> · {conditionContext.condition}{attributionMarket ? ` · ${attributionMarket}` : ''}
          </div>
        </div>
      );
    }
    return null;
  }

  // Warning rule (owner, 2026-10-09; threshold refined 2026-10-12): warn
  // ONLY when the entered price is STRICTLY above the DISPLAYED upper
  // endpoint, for every fresh kind — verdict, single reference, and ranges
  // including limited ones. One consistent rule, no deadband anywhere, and
  // the threshold is the number on screen rather than the stored cents (see
  // warnAboveCents above). Old-format pricing (kind 'legacy', covering v1
  // docs and stale v2) never warns.
  const aboveBand =
    (isVerdict || isSingle || evaluation.kind === 'range') &&
    hasPrice && warnAboveCents !== null && priceCents > warnAboveCents;
  const showHighPriceWarning = !compact && aboveBand;
  // Qualified body below verdict grade: no "average price" claim is
  // justified there, so the message states what was actually observed.
  // Verdict-grade warnings (including a qualified single, where the band
  // collapsed on verdict-grade evidence) keep the popover's default
  // "average price" line via fairPriceLabel.
  let warningMessage = null;
  if (evaluation.transactionInformed) {
    // Transaction-informed band (I-4): neither existing body line is
    // truthful here. The limited-kind messages name "Reverb listings"
    // (false for a band sold/trade evidence moved or built alone), and the
    // default line calls the displayed mid an "average" — but the blended
    // mid is a weighted-median mix, and the display-collision rule can
    // further replace it with the range midpoint; neither is an average of
    // the underlying transactions. So the warning states only what the
    // comparison actually established: the price is above the estimate.
    // COPY IS PROVISIONAL pending owner approval; the strict
    // price > high rule is unchanged.
    if (aboveBand) {
      warningMessage = isSingle
        ? 'Your price is above the estimated price for this pedal in this condition.'
        : 'Your price is above the estimated range for this pedal in this condition.';
    }
  } else if (aboveBand && !isVerdict && !(isSingle && evaluation.qualified === true)) {
    if (isSingle) {
      const n = evaluation.info && Number.isFinite(evaluation.info.totalListings)
        ? evaluation.info.totalListings : 1;
      warningMessage = n === 1
        ? `The one comparable Reverb listing we found is priced at ${formatDollarsFromCents(evaluation.priceCents)}.`
        : `The few comparable Reverb listings we found are priced at ${formatDollarsFromCents(evaluation.priceCents)}.`;
    } else {
      warningMessage = evaluation.info && evaluation.info.limited
        ? `The few comparable Reverb listings we found are priced between ${formatDollarsFromCents(tier.low)} and ${formatDollarsFromCents(tier.high)}.`
        : `Comparable Reverb listings are priced between ${formatDollarsFromCents(tier.low)} and ${formatDollarsFromCents(tier.high)}.`;
    }
  }

  // Compact variant — designed to sit inline next to a listing's price text
  // (e.g. PedalDetails). Owner decision 2026-10-09: ONE neutral numeric chip
  // for EVERY kind — "Market price $250" for a single reference, "Market
  // price $159–$230" otherwise, verdict-grade included. The old Good value /
  // Fair / High segment bar showed judgment words with no numbers while the
  // low-confidence states showed numbers with no judgment; the range is now
  // always the chip, and Good deal / Above market calls live in the Browse
  // badges (still verdict-gated). All disclosures ride in the tooltip.
  if (compact) {
    // 30px/14px (was originally 22px/11px): sized up twice by owner request —
    // the range text was hard to read on phones once the chip became the
    // universal numeric treatment.
    const cPillHeight  = 30;
    const cBorderWidth = 1;
    // Owner settled 2026-10-09 after trying gold variants: the whole chip
    // text is one neutral color — values match the "Market price" label.
    const chipValue = isSingle
      ? formatDollarsFromCents(evaluation.priceCents)
      : `${formatDollarsFromCents(tier.low)}–${formatDollarsFromCents(tier.high)}`;
    return (
      <div
        aria-label={accessiblePriceLabel}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: cPillHeight,
          padding: '0 14px',
          flexShrink: 0,
          borderRadius: cPillHeight / 2,
          border: `${cBorderWidth}px solid ${COLORS.inactiveBorder}`,
          backgroundColor: COLORS.inactiveBg,
          color: COLORS.inactiveText,
          fontFamily: 'Nunito, sans-serif',
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          userSelect: 'none',
          ...style
        }}
      >
        Market price&nbsp;<span>{chipValue}</span>
      </div>
    );
  }

  // Full variant, non-verdict kinds — the approved Part 13 presentation:
  // same gold card chrome, "Market price" wording, no Good/Fair/High words,
  // no zone bar, no invented spread. A small "Limited data" note marks tiny
  // samples and prior data; everything else is in the tooltip.
  if (!isVerdict) {
    // MOBILE WRAPPING (owner, 2026-10-12, rev 2): when the label and the
    // range cannot fit on one line the break must fall BETWEEN them —
    // "Market price" on one line, the COMPLETE range on the next — and
    // never inside either part.
    // WHY NON-BREAKING SPACES WERE NOT ENOUGH (rev 1 defect, observed in
    // the browser as "Market price $185 –" / "$210"): the en dash U+2013
    // carries Unicode line-break class BA ("break after"), so the browser
    // may break immediately AFTER the dash even when the spaces around it
    // are non-breaking. Only suppressing line breaking for the whole range
    // prevents that. The label and the range are therefore separate
    // elements, each internally non-wrapping, with one ordinary space
    // between them as the sole break opportunity.
    // Presentation only: the band, the caveat below it, and every warning
    // rule are untouched, and the accessible label is built separately
    // with ordinary spaces.
    const rangeText = isSingle
      ? formatDollarsFromCents(evaluation.priceCents)
      : `${formatDollarsFromCents(tier.low)} – ${formatDollarsFromCents(tier.high)}`;
    const showLimitedNote = shouldShowLimitedNote(evaluation);
    // Reverb attribution is shown only when Reverb asking data is actually
    // in the band. The SERVER decides this (R-1: the blend and its
    // contributions are private; marketDisplayV1 carries the one boolean
    // the UI needs). A transaction-only band contains no Reverb data:
    // attributing it would be false, and the ToS link-back applies to
    // Reverb data usage, which there is none of. This state never rendered
    // at all pre-I-4, so nothing visible changes on existing displays.
    const showReverbAttribution =
      !evaluation.transactionInformed || evaluation.showReverbAttribution !== false;
    return (
      <>
      <div
        style={{
          marginBottom: '1rem',
          fontFamily: 'Nunito, sans-serif',
          userSelect: 'none',
          ...style
        }}
        aria-label={accessiblePriceLabel}
      >
        <div
          style={{
            backgroundColor: COLORS.activeBg,
            border: `1.5px solid ${COLORS.activeBorder}`,
            borderRadius: 12,
            padding: '0.85rem 1rem',
            textAlign: 'center'
          }}
        >
          <div
            style={{
              fontSize: '1.45rem',
              fontWeight: 800,
              color: COLORS.activeText,
              lineHeight: 1.2
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>Market price</span>{' '}
            <span style={{ whiteSpace: 'nowrap' }}>{rangeText}</span>
          </div>
          {showLimitedNote && (
            <div
              style={{
                marginTop: 4,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: COLORS.attrText
              }}
            >
              {/* Owner display policy (2026-10-09): ONE generic visible
                  caveat in the existing Limited-data position — no counts,
                  no source breakdowns, no new interface elements. Detailed
                  evidence provenance stays internal. */}
              This estimate is based on limited comparable data.
            </div>
          )}
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: COLORS.attrText,
            textAlign: 'center'
          }}
        >
          {showReverbAttribution ? (
            <>
              <a
                href={reverbSearchUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: COLORS.rangeText,
                  textDecoration: 'underline',
                  textDecorationStyle: 'dotted',
                  textUnderlineOffset: '2px'
                }}
              >
                Reverb listings
              </a> · {condition}{attributionMarket ? ` · ${attributionMarket}` : ''}
            </>
          ) : (
            <>{condition}{attributionMarket ? ` · ${attributionMarket}` : ''}</>
          )}
        </div>
      </div>

      <HighPriceWarningPopover
        anchorRef={priceInputRef}
        visible={showHighPriceWarning && !warningDismissed}
        fairPriceLabel={isSingle
          ? formatDollarsFromCents(evaluation.priceCents)
          : formatDollarsFromCents(displayTier.mid)}
        message={warningMessage}
        onDismiss={() => setWarningDismissed(true)}
      />
      </>
    );
  }

  // Full variant, VERDICT-GRADE evidence (Add Pedal wizard Step 3, rendered
  // ABOVE the price input): the "Fair price $mid" headline with a
  // "Market price $low – $high" line beneath it, so the range is labelled
  // the same way as every other price surface. The compact pill variant is
  // unchanged.
  return (
    <>
    <div
      style={{
        marginBottom: '1rem',
        fontFamily: 'Nunito, sans-serif',
        userSelect: 'none',
        ...style
      }}
      aria-label={accessiblePriceLabel}
    >
      <div
        style={{
          backgroundColor: COLORS.activeBg,
          border: `1.5px solid ${COLORS.activeBorder}`,
          borderRadius: 12,
          padding: '0.85rem 1rem',
          textAlign: 'center'
        }}
      >
        <div
          style={{
            fontSize: '1.45rem',
            fontWeight: 800,
            color: COLORS.activeText,
            lineHeight: 1.2
          }}
        >
          Fair price {formatDollarsFromCents(displayTier.mid)}
        </div>
        <div
          style={{
            marginTop: 4,
            fontSize: '0.9rem',
            fontWeight: 600,
            color: COLORS.headerText
          }}
        >
          {/* Owner, 2026-10-12: the second line is labelled "Market price"
              so it matches every other price surface, and the "Good"/"High"
              labels are gone. The label and the COMPLETE range are separate
              non-wrapping elements, so a narrow screen can only break
              between them — never inside the range (the en dash is a
              Unicode break-after character, which is why nowrap, not
              non-breaking spaces, is what holds it together).
              Presentation only: the values, verdict gating, attribution,
              caveat rules and every warning boundary are unchanged. */}
          <span style={{ whiteSpace: 'nowrap' }}>Market price</span>{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            {formatDollarsFromCents(displayTier.low)} – {formatDollarsFromCents(displayTier.high)}
          </span>
        </div>
      </div>

      {/* Attribution — the "Reverb listings" portion links back to reverb.com
          per Reverb's API ToS link-back guidance. Condition + market portion
          stays plain text so users understand those are PXC-derived labels. */}
      <div
        style={{
          marginTop: 6,
          fontSize: 12,
          color: COLORS.attrText,
          textAlign: 'center'
        }}
      >
        <a
          href={reverbSearchUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: COLORS.rangeText,
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
            textUnderlineOffset: '2px'
          }}
        >
          Reverb listings
        </a> · {condition} · {formatMarketLabel(normDecade)}
      </div>
    </div>

      <HighPriceWarningPopover
        anchorRef={priceInputRef}
        visible={showHighPriceWarning && !warningDismissed}
        fairPriceLabel={formatDollarsFromCents(displayTier.mid)}
        onDismiss={() => setWarningDismissed(true)}
      />
    </>
  );
}

export default PriceIndicator;
